import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE } from '../api-base.token';
import { TrainingPlanResponse } from './training-plan-response';
import { CreateTrainingPlanRequest } from './create-training-plan-request';
import { UpdateTrainingPlanRequest } from './update-training-plan-request';
import { MemberTrainingResponse } from './member-training-response';
import { CompleteTrainingSubjectRequest } from './complete-training-subject-request';

@Injectable({
  providedIn: 'root'
})
export class AnnualRefresherTrainingHttpService {

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE) private readonly apiBase: string
  ) {}

  createTrainingPlan(request: CreateTrainingPlanRequest): Observable<TrainingPlanResponse> {
    return this.httpClient.post<TrainingPlanResponse>(
      `${this.apiBase}/AnnualRefresherTrainings/training-plans`,
      request
    );
  }

  updateTrainingPlan(id: string, request: UpdateTrainingPlanRequest): Observable<TrainingPlanResponse> {
    return this.httpClient.put<TrainingPlanResponse>(
      `${this.apiBase}/AnnualRefresherTrainings/training-plans/${id}`,
      request
    );
  }

  getTrainingPlan(id: string): Observable<TrainingPlanResponse> {
    return this.httpClient.get<TrainingPlanResponse>(
      `${this.apiBase}/AnnualRefresherTrainings/training-plans/${id}`
    );
  }

  getTrainingPlansByOrganization(organizationId: string): Observable<TrainingPlanResponse[]> {
    return this.httpClient.get<TrainingPlanResponse[]>(
      `${this.apiBase}/AnnualRefresherTrainings/organizations/${organizationId}/training-plans`
    );
  }

  getMemberTraining(organizationMemberId: string): Observable<MemberTrainingResponse> {
    return this.httpClient.get<MemberTrainingResponse>(
      `${this.apiBase}/AnnualRefresherTrainings/members/${organizationMemberId}/training`
    );
  }

  getOperatorTraining(workspaceMemberId: string): Observable<MemberTrainingResponse> {
    return this.httpClient.get<MemberTrainingResponse>(
      `${this.apiBase}/AnnualRefresherTrainings/operators/${workspaceMemberId}/training`
    );
  }

  completeTrainingSubject(request: CompleteTrainingSubjectRequest): Observable<string> {
    return this.httpClient.post<string>(
      `${this.apiBase}/AnnualRefresherTrainings/complete-subject`,
      request
    );
  }

  deleteTrainingPlan(id: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.apiBase}/AnnualRefresherTrainings/training-plans/${id}`
    );
  }

  getPendingSignoffs(
    competentPersonId: string, 
    trainingPlanId?: string,
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    sortBy: string = 'name',
    sortDescending: boolean = false
  ): Observable<any> {
    let url = `${this.apiBase}/AnnualRefresherTrainings/competent-persons/${competentPersonId}/pending-signoffs`;
    
    const params: string[] = [];
    if (trainingPlanId) params.push(`trainingPlanId=${trainingPlanId}`);
    params.push(`pageNumber=${pageNumber}`);
    params.push(`pageSize=${pageSize}`);
    if (searchTerm) params.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
    params.push(`sortBy=${sortBy}`);
    params.push(`sortDescending=${sortDescending}`);
    
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    
    return this.httpClient.get<any>(url);
  }

  bulkCompetentPersonSignoff(request: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.apiBase}/AnnualRefresherTrainings/competent-person-signoff/bulk`,
      request
    );
  }

  competentPersonSignoff(request: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.apiBase}/AnnualRefresherTrainings/competent-person-signoff`,
      request
    );
  }

  generateBulkAnnualRefresherReport(request: { organizationId: string; trainingPlanId: string; workspaceIds: string[] }): Observable<Blob> {
    return this.httpClient.post(
      `${this.apiBase}/AnnualRefresherTrainingCertificate/generate-bulk-report`,
      request,
      { responseType: 'blob' }
    );
  }
}
